// Calendário de estudos: formato dos dados, soma de tempo vinda do Ciclo e formatação.
// Extraído de App.js sem alteração de comportamento.
import { loadLS, saveLS } from "./storage";
import { sb } from "./supabase";
import { CAL_ALIASES } from "./constants";

// ═════════════════════════════════════════════════════════════════════════════
// ── CALENDÁRIO DE ESTUDOS ────────────────────────────────────────────────────
// Substitui a planilha PROJETO_PRF. Dados em MINUTOS, salvos na tabela
// "study_calendar" do Supabase (1 linha por usuário, coluna data = JSONB) e
// espelhados no localStorage como cópia de segurança.
//
// Formato de `data`:
//   days: { "2026-02-05": { "Língua Portuguesa": 105, ... }, ... }
//   bulk: { "2026-01": { "Língua Portuguesa": 360, ... } }   // totais SEM dia
//         (janeiro/2026 na planilha só tinha total por matéria, sem dias)
// ═════════════════════════════════════════════════════════════════════════════

export const calEmpty = () => ({ days: {}, bulk: {}, notes: {} });

const calKeyOf = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function calAllSubjects(d) {
  const set = new Set(d.subjects || []);
  Object.values(d.days || {}).forEach(o => Object.keys(o).forEach(k => set.add(k)));
  Object.values(d.bulk || {}).forEach(o => Object.keys(o).forEach(k => set.add(k)));
  return set;
}

// Tempo computado no Ciclo → soma no dia de hoje, na matéria do bloco.
// Fila para que dois registros seguidos não se atropelem.
let _calQueue = Promise.resolve();

export function calAddStudy(uid, subject, sec, when = new Date()) {
  if (!uid || !subject || !sec) return;                  // negativo = ajuste para menos ao finalizar a sessão
  _calQueue = _calQueue.then(async () => {
    try {
      const LS_KEY = "g17_study_calendar_" + uid;
      const { data: row, error } = await sb.from("study_calendar").select("data").eq("user_id", uid).maybeSingle();
      if (error) return;
      const base = (row && row.data && row.data.days) ? row.data : (loadLS(LS_KEY, null) || calEmpty());
      const d = { ...base, days: { ...base.days } };
      const known = calAllSubjects(d);
      const name = (CAL_ALIASES[subject] && known.has(CAL_ALIASES[subject])) ? CAL_ALIASES[subject] : subject;
      const k = calKeyOf(when);
      const day = { ...(d.days[k] || {}) };
      day[name] = Math.max(0, Math.round(((day[name] || 0) + sec / 60) * 100) / 100);
      if (!day[name]) delete day[name];
      d.days[k] = day;
      if (Array.isArray(d.subjects) && !d.subjects.includes(name)) d.subjects = [...d.subjects, name];
      const { error: e2 } = await sb.from("study_calendar").upsert({ user_id: uid, data: d, updated_at: new Date().toISOString() }, { onConflict: "user_id" });
      if (!e2) saveLS(LS_KEY, d);
    } catch (e) { console.error("calAddStudy:", e); }
  });
}

export const calIdx = (y, m) => y * 12 + m;         // posição do mês numa linha do tempo, para comparar meses

export function calFmtHM(min) {            // 105 -> "1:45"  (vazio se 0)
  min = Math.round(min || 0);       // o Ciclo grava frações de minuto
  if (!min) return "";
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`;
}

export function calFmtLong(min) {          // 6748 -> "112h28"
  min = Math.round(min || 0);
  const h = Math.floor(min / 60), m = min % 60;
  return `${h}h${String(m).padStart(2, "0")}`;
}

// Aceita "1:45", "1h45", "1h", "45min", "45m" ou só número (= minutos). "" = 0. Inválido = null.
export function calParse(str) {
  const s = String(str).trim().toLowerCase().replace(",", ".");
  if (s === "") return 0;
  let m;
  if ((m = s.match(/^(\d+):(\d{1,2})(?::\d{1,2})?$/))) return +m[1] * 60 + +m[2];
  if ((m = s.match(/^(\d+)\s*h\s*(?:(\d{1,2})\s*(?:m|min)?)?$/))) return +m[1] * 60 + (m[2] ? +m[2] : 0);
  if ((m = s.match(/^(\d+)\s*(?:m|min)$/))) return +m[1];
  if ((m = s.match(/^\d+$/))) return +s;
  return null;
}
